/**
 * WizardStepPreview — krok 6/6 kreatora widgetu: podgląd na żywo z bieżącego szkicu.
 *
 * `usePreviewWidget()` (bez wbudowanego toasta — patrz docstring hooka) liczony na WEJŚCIU w krok
 * (komponent odmontowuje się przy zmianie kroku w `WidgetCreatorWizard`, więc powrót tutaj po
 * zmianie metryk/filtrów zawsze liczy świeżo) + ręczny przycisk „Odśwież" dla tego samego configu.
 * Renderuje dokładnie te same kształty co host pulpitu (`CustomWidget.tsx`) — scalar/series/rows —
 * ale lokalnie (host jest read-only dla tego taska, nie eksportuje swoich sub-komponentów).
 */

import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { CustomWidgetChart } from '@/components/dashboard/widgets/CustomWidgetChart';
import { Spinner } from '@/components/Spinner';
import { usePreviewWidget } from '@/hooks/useWidgetDefinitions';
import type {
  WidgetComputeResult,
  WidgetDefinitionConfig,
  WidgetVisualType,
} from '@/lib/widgets/types';

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toLocaleString('pl-PL');
  if (typeof value === 'boolean') return value ? '✓' : '—';
  return String(value);
}

function PreviewResult({
  result,
  visualType,
}: {
  result: WidgetComputeResult;
  visualType: WidgetVisualType;
}) {
  const { t } = useTranslation();

  if (!result.ok) {
    const failure = result.failure;
    const params =
      failure.code === 'FIELD_NOT_ALLOWED'
        ? { field: failure.field }
        : failure.code === 'UNSUPPORTED'
          ? { reason: failure.reason }
          : undefined;
    return (
      <p className="flex h-full items-center justify-center px-4 text-center text-sm text-amber-600 dark:text-amber-400">
        {t(`tenant.widgets.wizard.preview.failure.${failure.code}`, params)}
      </p>
    );
  }

  const value = result.value;
  if (value.kind === 'scalar') {
    return (
      <div className="flex h-full flex-col items-center justify-center">
        <span className="text-4xl font-bold tabular-nums text-text-primary">
          {value.value === null ? '—' : value.value.toLocaleString('pl-PL')}
        </span>
      </div>
    );
  }
  if (value.kind === 'series') {
    if (value.items.length === 0) {
      return (
        <p className="flex h-full items-center justify-center text-sm text-text-muted">
          {t('tenant.widgets.wizard.preview.empty')}
        </p>
      );
    }
    return <CustomWidgetChart items={value.items} visualType={visualType} />;
  }
  // value.kind === 'rows'
  if (value.rows.length === 0) {
    return (
      <p className="flex h-full items-center justify-center text-sm text-text-muted">
        {t('tenant.widgets.wizard.preview.empty')}
      </p>
    );
  }
  return (
    <div className="h-full w-full overflow-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="border-b border-border-secondary text-left text-text-tertiary">
            {value.columns.map((c) => (
              <th key={c.field} className="px-2 py-1 font-medium">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {value.rows.map((row, idx) => (
            <tr key={idx} className="border-b border-border-secondary/50">
              {value.columns.map((c) => (
                <td key={c.field} className="px-2 py-1 text-text-secondary">
                  {formatCell(row[c.field])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export interface WizardStepPreviewProps {
  visualType: WidgetVisualType;
  source: string;
  config: WidgetDefinitionConfig;
}

export function WizardStepPreview({ visualType, source, config }: WizardStepPreviewProps) {
  const { t } = useTranslation();
  const mutation = usePreviewWidget();

  useEffect(() => {
    mutation.mutate({ visualType, source, config });
    // Liczymy na KAŻDE wejście w ten krok (mount) — patrz docstring modułu. Bez `config`/`source`/
    // `visualType` w deps: to celowo "raz na wejście", odświeżenie w locie robi przycisk niżej.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-3" data-testid="widget-wizard-preview">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm text-text-tertiary">{t('tenant.widgets.wizard.preview.hint')}</p>
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<RefreshCw className="h-3.5 w-3.5" />}
          loading={mutation.isPending}
          onClick={() => mutation.mutate({ visualType, source, config })}
          data-testid="widget-wizard-preview-refresh"
        >
          {t('tenant.widgets.wizard.preview.refresh')}
        </Button>
      </div>
      <Card padding="sm" className="min-h-[13rem]" data-testid="widget-wizard-preview-panel">
        <div className="h-52">
          {mutation.isPending ? (
            <div className="flex h-full items-center justify-center">
              <Spinner />
            </div>
          ) : mutation.isError ? (
            <p className="flex h-full items-center justify-center px-4 text-center text-sm text-red-600">
              {t('tenant.widgets.wizard.preview.httpError')}
            </p>
          ) : mutation.data ? (
            <PreviewResult result={mutation.data} visualType={visualType} />
          ) : null}
        </div>
      </Card>
    </div>
  );
}
